import '../../assets/css/case-property.css';
// @ts-ignore
import { useProperty, useContentToken, EditableToken, useGallery, useVirtualTours, usePropertyAddressUrl } from '@vibesites/runtime/react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { PhoneIcon, LocationIcon, CameraIcon } from '@vibesites/assets/icons/ui';
import { formatPhone, phoneDigits, getPropertyBasePath } from './propertyUtils';
import { useFilteredGalleryIndices } from './useFilteredGallery';
import { openTourScheduler } from './TourSchedulerModal';

/**
 * "Life Untethered at {{property_name}}" footer CTA with 3 info cards.
 * Matches the Stratman wp-block-stratman-footer-cta property-pre-footer pattern.
 */
export function PropertyFooterCTA() {
  const property = useProperty();
  const gallery = useGallery();
  const virtualTours = useVirtualTours();

  const name = useContentToken('propertyfootercta.property_name') || property.property_name || '';
  const phone = useContentToken('propertyfootercta.phone') || property.property_call_tracking_phone || property.property_phone || '';
  const address = useContentToken('propertyfootercta.address') || property.property_address_1 || property.property_address || '';
  const city = (property.property_address_city || '').trim();
  const state = (property.property_address_state || '').trim();
  const zip = (property.property_address_zip || '').trim();
  const basePath = getPropertyBasePath(property);
  const tourUrlToken = useContentToken('propertyfootercta.tour_url');
  const tourUrl = tourUrlToken
    ? ((tourUrlToken.startsWith('/') || tourUrlToken.startsWith('http'))
      ? tourUrlToken
      : `${basePath}/${tourUrlToken}`)
    : '';
  const tourEvent = useContentToken('propertyfootercta.tour_event');

  const fi = useFilteredGalleryIndices();
  const cardImage0 = useContentToken('propertyfootercta.card_image_0') || (gallery[fi[0]] as any)?.image || '';
  const cardImage1 = useContentToken('propertyfootercta.card_image_1') || (gallery[fi[1]] as any)?.image || '';
  const cardImage2 = useContentToken('propertyfootercta.card_image_2') || (gallery[fi[2]] as any)?.image || '';

  const mapsUrl = usePropertyAddressUrl();

  return (
    <section className="prop-footer-cta">
      <div className="prop-container-wide">
        <div className="prop-footer-cta-heading">
          <EditableToken path="propertyfootercta.label" type="text" label="Label Text" as="div" className="label" defaultValue={contentSchema.tokens['propertyfootercta.label'].defaultValue}>
            {useContentToken('propertyfootercta.label') || 'Life Untethered at'}
          </EditableToken>
          <EditableToken path="propertyfootercta.property_name" type="text" label="Property Name" as="h2" className="prop-box-separator" defaultField={contentSchema.tokens['propertyfootercta.property_name'].defaultField}>
            {name}
          </EditableToken>
        </div>

        <div className="prop-cta-cards">
          {/* Call Us */}
          {phone && (
            <div className="prop-cta-card">
              <EditableToken path="propertyfootercta.card_image_0" type="image" label="Call Us Background" as="div" className="prop-cta-card-bg-wrap" defaultField={contentSchema.tokens['propertyfootercta.card_image_0'].defaultField}>
                {cardImage0 && <VibesitesImage className="prop-cta-card-bg" url={cardImage0} alt="" sizes="(min-width: 992px) 33vw, (min-width: 768px) 50vw, 100vw" />}
              </EditableToken>
              <EditableToken path="propertyfootercta.phone" type="phone" label="Phone Number" as="div" className="prop-cta-card-content" defaultField={contentSchema.tokens['propertyfootercta.phone'].defaultField}>
                <a href={`tel:${phoneDigits(phone)}`} className="prop-cta-card-stacked">
                  <div className="prop-cta-card-heading-row">
                    <span className="prop-cta-card-icon">
                      <PhoneIcon size={32} />
                    </span>
                    <h3>Call Us</h3>
                  </div>
                  <span>{formatPhone(phone)}</span>
                </a>
              </EditableToken>
            </div>
          )}

          {/* Visit Us */}
          {address && (
            <div className="prop-cta-card">
              <EditableToken path="propertyfootercta.card_image_1" type="image" label="Visit Us Background" as="div" className="prop-cta-card-bg-wrap" defaultField={contentSchema.tokens['propertyfootercta.card_image_1'].defaultField}>
                {cardImage1 && <VibesitesImage className="prop-cta-card-bg" url={cardImage1} alt="" sizes="(min-width: 992px) 33vw, (min-width: 768px) 50vw, 100vw" />}
              </EditableToken>
              <EditableToken path="propertyfootercta.address" type="text" label="Address" as="div" className="prop-cta-card-content" defaultField={contentSchema.tokens['propertyfootercta.address'].defaultField}>
                <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="prop-cta-card-stacked">
                  <div className="prop-cta-card-heading-row">
                    <span className="prop-cta-card-icon">
                      <LocationIcon size={32} />
                    </span>
                    <h3>Visit Us</h3>
                  </div>
                  <p>{address}, {[city, state].filter(Boolean).join(', ')}{zip ? ` ${zip}` : ''}</p>
                </a>
              </EditableToken>
            </div>
          )}

          {/* Take a Tour */}
          <div className="prop-cta-card">
            <EditableToken path="propertyfootercta.card_image_2" type="image" label="Tour Background" as="div" className="prop-cta-card-bg-wrap" defaultField={contentSchema.tokens['propertyfootercta.card_image_2'].defaultField}>
              {cardImage2 && <VibesitesImage className="prop-cta-card-bg" url={cardImage2} alt="" sizes="(min-width: 992px) 33vw, (min-width: 768px) 50vw, 100vw" />}
            </EditableToken>
            <EditableToken path="propertyfootercta.tour_url" type="url" label="Tour Link URL" as="div" className="prop-cta-card-content" defaultValue={contentSchema.tokens['propertyfootercta.tour_url'].defaultValue}>
              {tourUrl ? (
                <a href={tourUrl} className="prop-cta-card-stacked" data-vibesite-event={tourEvent || undefined}>
                  <TourCardContent />
                </a>
              ) : (
                <a href="#schedule-a-tour" className="prop-cta-card-stacked" data-vibesite-event={tourEvent || undefined} onClick={(e) => { e.preventDefault(); openTourScheduler(); }}>
                  <TourCardContent />
                </a>
              )}
            </EditableToken>
          </div>
        </div>

      </div>
    </section>
  );
}

function TourCardContent() {
  return (
    <>
      <div className="prop-cta-card-heading-row">
        <span className="prop-cta-card-icon">
          <CameraIcon size={32} />
        </span>
        <h3>
          <EditableToken path="propertyfootercta.tour_title" type="text" label="Tour Card Title" as="span" defaultValue={contentSchema.tokens['propertyfootercta.tour_title'].defaultValue}>
            {useContentToken('propertyfootercta.tour_title') || 'Schedule a Tour'}
          </EditableToken>
        </h3>
      </div>
      <p>
        <EditableToken path="propertyfootercta.tour_subtitle" type="text" label="Tour Card Subtitle" as="span" defaultValue={contentSchema.tokens['propertyfootercta.tour_subtitle'].defaultValue}>
          {useContentToken('propertyfootercta.tour_subtitle') || 'Book Your Visit'}
        </EditableToken>
      </p>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyFooterCTA',
  displayName: 'Property Footer CTA',
  tokens: {
    'propertyfootercta.label': {
      label: 'Label Text',
      type: 'text',
      defaultValue: 'Life Untethered at',
    },
    'propertyfootercta.property_name': {
      label: 'Property Name',
      type: 'text',
      defaultField: 'property_name',
    },
    'propertyfootercta.phone': {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_phone',
    },
    'propertyfootercta.address': {
      label: 'Address',
      type: 'text',
      defaultField: 'property_address_1',
    },
    'propertyfootercta.tour_url': {
      label: 'Tour Link URL',
      type: 'url',
      description: 'Optional override for the tour card. Leave blank to open the schedule tour modal. Relative paths are resolved against the property base path.',
      defaultValue: '',
    },
    'propertyfootercta.tour_event': {
      label: 'Tour CTA Tracked Event',
      type: 'event',
      defaultValue: 'schedule_tour_clicked',
      description: 'Canonical event fired when a visitor clicks the Take a Tour card. Set to "None" to disable tracking on this CTA.',
    },
    'propertyfootercta.tour_title': {
      label: 'Tour Card Title',
      type: 'text',
      defaultValue: 'Schedule a Tour',
    },
    'propertyfootercta.tour_subtitle': {
      label: 'Tour Card Subtitle',
      type: 'text',
      defaultValue: 'Book Your Visit',
    },
    'propertyfootercta.card_image_0': {
      label: 'Call Us Card Background',
      type: 'image',
      description: 'Background image for the Call Us card. Defaults to photo_gallery[0].',
      defaultField: 'photo_gallery[0].image',
    },
    'propertyfootercta.card_image_1': {
      label: 'Visit Us Card Background',
      type: 'image',
      description: 'Background image for the Visit Us card. Defaults to photo_gallery[1].',
      defaultField: 'photo_gallery[1].image',
    },
    'propertyfootercta.card_image_2': {
      label: 'Tour Card Background',
      type: 'image',
      description: 'Background image for the Take a Tour card. Defaults to photo_gallery[2].',
      defaultField: 'photo_gallery[2].image',
    },
  },
};

(PropertyFooterCTA as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PropertyFooterCTA;
